import { useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

export const useAccessGrants = (userId, { onUnlockNotify, onSubscribeNotify } = {}) => {
  const [grants, setGrants] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);

  useEffect(() => {
    if (!userId) return;
    fetchGrants();
    fetchSubscriptions();
  }, [userId]);

  const fetchGrants = async () => {
    try {
      const { data } = await supabase
        .from('d2f_access_grants')
        .select('content_id')
        .eq('user_id', userId);
      setGrants(data?.map(g => g.content_id) || []);
    } catch {
      setGrants([]);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const { data } = await supabase
        .from('d2f_subscriptions')
        .select('artist_id, tier_id, status')
        .eq('user_id', userId)
        .eq('status', 'active');
      setSubscriptions(data || []);
    } catch {
      setSubscriptions([]);
    }
  };

  const hasAccess = (contentId) => grants.includes(contentId);
  const hasSubscription = (artistId) => subscriptions.some(s => s.artist_id === artistId);
  const getSubscriptionTier = (artistId) => subscriptions.find(s => s.artist_id === artistId)?.tier_id;

  const grantAccess = async ({ userId, contentId, artistId, amount, contentTitle }) => {
    try {
      const { error } = await supabase.from('d2f_access_grants').upsert({
        user_id: userId,
        content_id: contentId,
        artist_id: artistId,
        amount_paid: parseFloat(amount) || 0,
      });
      if (!error) {
        setGrants(prev => [...prev, contentId]);
        // Persist notification
        await supabase.from('d2f_notifications').insert({
          user_id: userId,
          type: 'unlock',
          title: `"${contentTitle || 'Content'}" Unlocked`,
          message: 'You now have full access to this exclusive content.',
          content_id: contentId,
          artist_id: artistId,
          metadata: { amount: parseFloat(amount) || 0 },
        });
        // Toast callback
        onUnlockNotify?.({
          type: 'unlock',
          title: `"${contentTitle || 'Content'}" Unlocked`,
          message: 'You now have full access to this exclusive content.',
          metadata: { amount: parseFloat(amount) || 0 },
        });
      }
      return { error };
    } catch (e) {
      return { error: e };
    }
  };

  const subscribeTier = async ({ userId, artistId, tierId, amount, tierName, artistName }) => {
    try {
      const expiresAt = new Date();
      expiresAt.setMonth(expiresAt.getMonth() + 1);
      const { error } = await supabase.from('d2f_subscriptions').upsert({
        user_id: userId,
        artist_id: artistId,
        tier_id: tierId,
        status: 'active',
        amount_paid: parseFloat(amount) || 0,
        started_at: new Date().toISOString(),
        expires_at: expiresAt.toISOString(),
      }, { onConflict: 'user_id,artist_id' });
      if (!error) {
        await fetchSubscriptions();
        // Persist notification
        await supabase.from('d2f_notifications').insert({
          user_id: userId,
          type: 'subscription',
          title: `${tierName || 'Membership'} Activated`,
          message: `You're now supporting ${artistName || 'this artist'} at $${amount}/mo.`,
          artist_id: artistId,
          metadata: { amount: parseFloat(amount) || 0, tier_name: tierName, artist_name: artistName },
        });
        // Toast callback
        onSubscribeNotify?.({
          type: 'subscription',
          title: `${tierName || 'Membership'} Activated`,
          message: `You're now supporting ${artistName || 'this artist'} at $${amount}/mo.`,
          metadata: { amount: parseFloat(amount) || 0 },
        });
      }
      return { error };
    } catch (e) {
      return { error: e };
    }
  };

  return { grants, subscriptions, hasAccess, hasSubscription, getSubscriptionTier, grantAccess, subscribeTier, refetch: fetchGrants };
};